import {useQuery} from '@tanstack/react-query';
import {BackendResponse} from '@common/http/backend-response/backend-response';
import {prettyQuotaBytes} from '@common/uploads/utils/pretty-bytes';
import {DriveQueryKeys} from '../../../drive-query-keys';
import {apiClient} from '@common/http/query-client';

export const DEFAULT_STORAGE_QUOTA = {
  available: 5 * 1024 * 1024 * 1024,
  used: 2 * 1024 * 1024 * 1024,
} as const;

export const DEFAULT_TRANSFER_QUOTA = {
  available: 10 * 1024 * 1024 * 1024,
  used: 3 * 1024 * 1024 * 1024,
} as const;

export interface FetchStorageSummaryResponse extends BackendResponse {
  used: number;
  available: number;
  transfer_used?: number;
  /** null when unlimited (e.g. billing off or no cap). */
  transfer_available?: number | null;
  storage_bonus_bytes?: number;
  plan_storage_bytes?: number | null;
}

export interface StorageSummaryFormatted {
  usedFormatted: string;
  availableFormatted: string | null;
  percentage: number;
  used: number;
  available: number | null;
  transferUsed: number;
  transferAvailable: number | null;
  transferUsedFormatted: string;
  transferAvailableFormatted: string | null;
  transferPercentage: number;
  storageBonusBytes?: number;
  planStorageBytes?: number | null;
}

function clampPercentage(value: number) {
  return Math.max(0, Math.min(100, value));
}

export function useStorageSummary() {
  return useQuery(DriveQueryKeys.fetchStorageSummary, fetchStorageSummary, {
    select: formatResponse,
  });
}

export function fetchStorageSummary(): Promise<FetchStorageSummaryResponse> {
  return apiClient.get(`user/space-usage`).then(response => response.data);
}

function formatResponse(
  response: FetchStorageSummaryResponse,
): StorageSummaryFormatted {
  // null means that user has unlimited space available
  const percentage =
    response.available === null
      ? 0
      : (response.used * 100) / response.available;

  const transferUsed = response.transfer_used ?? 0;
  const transferAvailable = response.transfer_available ?? null;
  const transferPercentage =
    transferAvailable === null
      ? 0
      : transferAvailable === 0
        ? 0
        : (transferUsed * 100) / transferAvailable;

  return {
    usedFormatted: prettyQuotaBytes(response.used),
    availableFormatted: prettyQuotaBytes(response.available),
    percentage,
    used: response.used,
    available: response.available,
    transferUsed,
    transferAvailable,
    transferUsedFormatted: prettyQuotaBytes(transferUsed),
    transferAvailableFormatted:
      transferAvailable === null || transferAvailable === undefined
        ? null
        : prettyQuotaBytes(transferAvailable),
    transferPercentage,
    storageBonusBytes: response.storage_bonus_bytes ?? 0,
    planStorageBytes: response.plan_storage_bytes ?? null,
  };
}

export function resolveStorageQuotaDisplay(
  summary: StorageSummaryFormatted | undefined,
  options: {
    userAvailableSpace?: number | null;
    monthlyTransferBytes?: number | null;
  } = {},
): StorageSummaryFormatted {
  const storageAvailable =
    summary?.available ??
    options.userAvailableSpace ??
    DEFAULT_STORAGE_QUOTA.available;
  const storageUsed = summary?.used ?? DEFAULT_STORAGE_QUOTA.used;
  const transferUsed = summary?.transferUsed ?? DEFAULT_TRANSFER_QUOTA.used;
  const transferAvailable =
    summary?.transferAvailable ??
    options.monthlyTransferBytes ??
    DEFAULT_TRANSFER_QUOTA.available;

  const storagePercentage = clampPercentage(
    summary?.percentage ??
      (storageAvailable ? (storageUsed * 100) / storageAvailable : 0),
  );
  const transferPercentage = clampPercentage(
    summary?.transferPercentage ??
      (transferAvailable ? (transferUsed * 100) / transferAvailable : 0),
  );

  return {
    used: storageUsed,
    available: storageAvailable,
    usedFormatted: prettyQuotaBytes(storageUsed),
    availableFormatted:
      storageAvailable === null ? null : prettyQuotaBytes(storageAvailable),
    percentage: storagePercentage,
    transferUsed,
    transferAvailable,
    transferUsedFormatted: prettyQuotaBytes(transferUsed),
    transferAvailableFormatted:
      transferAvailable === null ? null : prettyQuotaBytes(transferAvailable),
    transferPercentage,
    storageBonusBytes: summary?.storageBonusBytes ?? 0,
    planStorageBytes: summary?.planStorageBytes ?? null,
  };
}
